export interface PendingCheckout {
  planId: string;
  billing: 'monthly' | 'yearly';
  provider: 'dodo' | 'paypal';
  lang: string;
  ts: number;
  /** After login, CheckoutResumer may send the user straight to payment. */
  resumeAfterAuth?: boolean;
}

const KEY = 'power-toefl-pending-checkout';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
/** Auto-open checkout after login only for this window, not the next day. */
export const AUTH_RESUME_MAX_MS = 15 * 60 * 1000;

function readRaw(storage: Storage): PendingCheckout | null {
  try {
    const raw = storage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingCheckout;
    if (!parsed?.planId || Date.now() - (parsed.ts || 0) > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export const setPendingCheckout = (intent: Omit<PendingCheckout, 'ts'>) => {
  const raw = JSON.stringify({ ...intent, ts: Date.now() });
  try { sessionStorage.setItem(KEY, raw); } catch { /* ignore */ }
  try { localStorage.setItem(KEY, raw); } catch { /* ignore */ }
};

export const peekPendingCheckout = (): PendingCheckout | null => {
  return readRaw(sessionStorage) || readRaw(localStorage);
};

export const clearResumeAfterAuth = () => {
  const parsed = peekPendingCheckout();
  if (!parsed?.resumeAfterAuth) return;
  setPendingCheckout({
    planId: parsed.planId,
    billing: parsed.billing,
    provider: parsed.provider,
    lang: parsed.lang,
    resumeAfterAuth: false,
  });
};

/** One-shot: strip the flag so a later login does not open checkout. */
export const consumeResumeAfterAuth = (): PendingCheckout | null => {
  const parsed = peekPendingCheckout();
  if (!parsed?.resumeAfterAuth) return null;
  if (Date.now() - (parsed.ts || 0) > AUTH_RESUME_MAX_MS) {
    clearResumeAfterAuth();
    return null;
  }
  clearResumeAfterAuth();
  return parsed;
};

export const consumePendingCheckout = (): PendingCheckout | null => {
  const parsed = peekPendingCheckout();
  clearPendingCheckout();
  return parsed;
};

export const clearPendingCheckout = () => {
  try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
};
